import { MUI_X_LICENSE } from "appConfiguration";
import { reactPlugin } from "appInsightsWeb";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { AppCacheProvider } from "@mui/material-nextjs/v15-pagesRouter";
import { LicenseInfo } from "@mui/x-license";
import { ApplicationLoadingDisplay } from "core/components/ApplicationLoadingDisplay";
import { PageErrorBoundary } from "core/components/PageErrorBoundary";
import { PitstopApp } from "core/components/PitstopApp";
import { PublicApp } from "core/components/PublicApp";
import { SharedAppWrapper } from "core/components/SharedAppWrapper";
import type { BrandCode, RegionCode } from "core/entities";
import type { PublicRoutes } from "core/entities/APITypes";
import { ApplicationConfigurationErrorPage } from "core/pages/ApplicationConfigurationErrorPage";
import App, {
  type AppContext,
  type AppInitialProps,
  type AppProps,
} from "next/app";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import "i18n.config";

const NPS_PUBLIC_PATH: PublicRoutes = "/public/nps";

// https://mui.com/components/data-grid/getting-started/#license-key-installation
LicenseInfo.setLicenseKey(MUI_X_LICENSE);

type ApplicationMainProps = {
  config:
    | {
        brandCode: BrandCode;
        availableRegions: RegionCode[];
      }
    | { error: { message: string } };
};

export default function ApplicationMain(
  props: AppProps & ApplicationMainProps
) {
  const { Component, pageProps, config } = props;
  const { pathname } = useRouter();

  const [initialConfig] = useState(config);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  const currentBrandCode =
    "brandCode" in initialConfig ? initialConfig.brandCode : null;

  if (isInitialLoad) {
    return (
      <AppCacheProvider {...props}>
        <SharedAppWrapper brandCode={currentBrandCode}>
          <ApplicationLoadingDisplay />
        </SharedAppWrapper>
      </AppCacheProvider>
    );
  }

  if ("error" in initialConfig) {
    reactPlugin.trackException({
      severityLevel: SeverityLevel.Error,
      properties: { component: "_app", config: initialConfig },
    });
    return (
      <AppCacheProvider {...props}>
        <SharedAppWrapper brandCode={currentBrandCode}>
          <ApplicationConfigurationErrorPage
            message={initialConfig.error.message}
          />
        </SharedAppWrapper>
      </AppCacheProvider>
    );
  }

  const { availableRegions, brandCode } = initialConfig;

  const isPublic = pathname.split("/").at(1) === "public";
  if (pathname === NPS_PUBLIC_PATH) {
    return (
      <AppCacheProvider {...props}>
        <SharedAppWrapper
          brandCode={currentBrandCode}
          availableRegions={availableRegions}
        >
          <PageErrorBoundary>
            <Component {...pageProps} />
          </PageErrorBoundary>
        </SharedAppWrapper>
      </AppCacheProvider>
    );
  }
  if (isPublic) {
    return (
      <AppCacheProvider {...props}>
        <SharedAppWrapper
          brandCode={currentBrandCode}
          availableRegions={availableRegions}
        >
          <PublicApp Component={Component} pageProps={pageProps} />
        </SharedAppWrapper>
      </AppCacheProvider>
    );
  }

  return (
    <AppCacheProvider {...props}>
      <PitstopApp
        brandCode={brandCode}
        availableRegions={availableRegions}
        Component={Component}
        pageProps={pageProps}
      />
    </AppCacheProvider>
  );
}

ApplicationMain.getInitialProps = async (
  context: AppContext
): Promise<AppInitialProps & ApplicationMainProps> => {
  // https://nextjs.org/docs/advanced-features/custom-app
  const appProps = await App.getInitialProps(context);
  const host = context.ctx.req?.headers?.host;
  if (host) {
    const { getPitstopConfig } = await import("core/utils/getPitstopConfig");
    const config = await getPitstopConfig(host);
    return { ...appProps, config };
  }
  return {
    ...appProps,
    config: { error: { message: "client side getInitialProps call" } },
  };
};
